import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import { ownerApi } from "@/lib/api";
import { money } from "@/lib/format";
import { Spinner } from "@/components/common/States";
import { Switch } from "@/components/ui/switch";

export default function OwnerPrices() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["ownerServices"], queryFn: ownerApi.getServices });
  const shopQuery = useQuery({ queryKey: ["shop"], queryFn: () => ownerApi.getSettings() });
  const categories = shopQuery.data?.categories ?? [];
  const [adding, setAdding] = useState(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["ownerServices"] });
  const update = (sid, patch) => ownerApi.updateService(sid, patch).then(invalidate).catch((e) => toast.error(e.message));
  const create = useMutation({
    mutationFn: (payload) => ownerApi.createService(payload),
    onSuccess: () => { invalidate(); setAdding(null); toast.success("Item added"); },
    onError: (e) => toast.error(e.message),
  });
  const del = (sid) => ownerApi.deleteService(sid).then(invalidate).catch((e) => toast.error(e.message));

  if (query.isPending) return <Spinner />;
  const byCat = {};
  for (const s of query.data) (byCat[s.category] ??= []).push(s);

  return (
    <div className="space-y-6">
      <button onClick={() => setAdding({ name: "", category: categories[0] || "Dry Clean", price: 0, unit: "per piece", description: "", popular: false, active: true, sale_price: null })} className="inline-flex items-center gap-1 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"><Plus className="h-4 w-4" /> Add item</button>
      {Object.entries(byCat).map(([cat, items]) => (
        <div key={cat}>
          <h3 className="mb-2 font-display text-sm font-bold">{cat}</h3>
          <div className="space-y-2">
            {items.map((s) => (
              <div key={s.service_id} className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-3">
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{s.name}</span>
                <span className="text-xs text-muted-foreground">{s.unit}</span>
                <input type="number" defaultValue={s.price} onBlur={(e) => Number(e.target.value) !== s.price && update(s.service_id, { price: Number(e.target.value) })} className="w-20 rounded-lg border border-border p-1.5 text-sm" />
                <label className="flex items-center gap-1 text-xs">Active <Switch checked={s.active} onCheckedChange={(v) => update(s.service_id, { active: v })} /></label>
                <label className="flex items-center gap-1 text-xs">Popular <Switch checked={s.popular} onCheckedChange={(v) => update(s.service_id, { popular: v })} /></label>
                <button onClick={() => del(s.service_id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>
      ))}

      {adding ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setAdding(null)}>
          <div className="w-full max-w-sm space-y-3 rounded-2xl bg-card p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold">New item</h3>
            <input placeholder="Name" value={adding.name} onChange={(e) => setAdding({ ...adding, name: e.target.value })} className="w-full rounded-xl border border-border p-2.5 text-sm" />
            <select value={adding.category} onChange={(e) => setAdding({ ...adding, category: e.target.value })} className="w-full rounded-xl border border-border bg-background p-2.5 text-sm">
              {categories.map((c) => <option key={c}>{c}</option>)}
            </select>
            <div className="flex gap-2">
              <input type="number" placeholder="Price" value={adding.price} onChange={(e) => setAdding({ ...adding, price: Number(e.target.value) })} className="w-full rounded-xl border border-border p-2.5 text-sm" />
              <input placeholder="Unit" value={adding.unit} onChange={(e) => setAdding({ ...adding, unit: e.target.value })} className="w-full rounded-xl border border-border p-2.5 text-sm" />
            </div>
            <button onClick={() => create.mutate(adding)} disabled={!adding.name || create.isPending} className="w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">Add item</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
